import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { withNamespaces } from 'react-i18next';
import {
    Card,
    CardBody,
} from 'reactstrap';

import useMailboxActions from '../../hooks/useMailboxActions';
import useMailboxBackPath from '../../hooks/useMailboxBackPath';
import useMailboxIntentPreview from '../../hooks/useMailboxIntentPreview';
import {
    getMailboxComposePath,
    buildMailboxReplyDraft,
    getMessageId,
    getNewestThreadMessage,
    getThreadFolderActionIds,
    getUnreadThreadMessageIds,
    hasMailboxThread,
} from '../../helpers';
import MailboxBackButton from '../mailbox-back-button';
import MessageIntentAnalysisModal from '../message-intent-analysis-modal';
import MessageActions from './components/MessageActions';
import MessageAttachments from './components/MessageAttachments';
import MessageBody from './components/MessageBody';
import MessageConversation from './components/MessageConversation';
import MessageHeader from './components/MessageHeader';
import MessageIntentActions from './components/MessageIntentActions';
import MessageLinkedCases from './components/MessageLinkedCases';
import MessageReply from './components/MessageReply';
import MessageSkeleton from './components/MessageSkeleton';
import useMailboxAttachments from './hooks/useMailboxAttachments';
import useMailboxIntents from './hooks/useMailboxIntents';
import useMailboxMessage from './hooks/useMailboxMessage';
import useMailboxRoleAccess from 'src/modules/mailer/hooks/useMailboxRoleAccess';

const MessageDetail = ({
    t,
    messageId,
}) => {
    const history = useHistory();
    const location = useLocation();
    const {
        backFolder,
        backFolderTitleKey,
        goBack,
    } = useMailboxBackPath();
    const {
        message,
        isLoading,
        isFetching,
        isError,
    } = useMailboxMessage({
        t,
        messageId,
    });
    const {
        regularAttachments,
        rewrittenHtml,
        previewUrls,
        downloadAttachment,
        downloadingId,
    } = useMailboxAttachments({
        t,
        message,
    });
    const {
        intents,
        cases,
        isApplying,
        applyIntent,
    } = useMailboxIntents({
        t,
        messageId,
        message,
    });
    const preview = useMailboxIntentPreview({
        t,
        intents,
        applyCase: ({ intent, analysis }) => applyIntent(intent, analysis),
    });
    const {
        isActing,
        updateFlags,
        trashMessages,
        restoreMessages,
    } = useMailboxActions({
        t,
        onMoved: goBack,
    });
    const { canApplyIntents } = useMailboxRoleAccess();
    const markedReadRef = useRef(null);

    useEffect(() => {
        markedReadRef.current = null;
    }, [messageId]);

    useEffect(() => {
        const unreadIds = getUnreadThreadMessageIds(message);
        const key = unreadIds.map(String).join(',');

        if (!unreadIds.length || markedReadRef.current === key) {
            return;
        }

        markedReadRef.current = key;
        updateFlags(unreadIds, { unread: false });
    }, [message, updateFlags]);

    if ((isLoading || isFetching) && !message) {
        return <MessageSkeleton />;
    }

    if (isError || !message) {
        return (
            <Card>
                <CardBody>
                    <MailboxBackButton
                        folderTitleKey={backFolderTitleKey}
                        onClick={goBack}
                    />
                    <p className="text-muted mb-0">
                        {t('This message could not be found.')}
                    </p>
                </CardBody>
            </Card>
        );
    }

    const id = getMessageId(message) || messageId;
    const showThread = hasMailboxThread(message);
    const folderActionIds = getThreadFolderActionIds(message);
    const actionsBusy = isActing || isApplying || preview.isBusy;
    const handleReply = () => {
        history.push({
            pathname: getMailboxComposePath(),
            state: {
                mailboxFolderPath: location.state?.mailboxFolderPath,
                mailboxComposeBackPath: location.pathname,
                mailboxReply: buildMailboxReplyDraft(getNewestThreadMessage(message), {
                    folder: backFolder,
                }),
            },
        });
    };

    return (
        <Card>
            <CardBody className="mailbox-message-detail">
                <div className="mailbox-message-detail__toolbar">
                    <MailboxBackButton
                        folderTitleKey={backFolderTitleKey}
                        onClick={goBack}
                    />
                    <div className="d-flex align-items-center flex-wrap justify-content-end ms-auto mailbox-message-detail__toolbar-actions">
                        {canApplyIntents ? (
                            <MessageIntentActions
                                intents={intents}
                                cases={cases}
                            />
                        ) : null}
                        <MessageActions
                            message={message}
                            intents={intents}
                            cases={cases}
                            isActing={actionsBusy}
                            onTrash={() => trashMessages(folderActionIds)}
                            onRestore={() => restoreMessages(folderActionIds)}
                            onToggleStar={(starred) => updateFlags(folderActionIds, { starred })}
                            onApplyIntent={(intent) => preview.startPreview({
                                messageId: id,
                                intent,
                                existingCases: cases,
                                message,
                            })}
                        />
                    </div>
                </div>
                <MessageLinkedCases cases={cases} />
                {showThread ? (
                    <MessageConversation
                        message={message}
                        t={t}
                    />
                ) : (
                    <>
                        <MessageHeader message={message} />
                        <MessageBody
                            message={message}
                            html={rewrittenHtml}
                        />
                        <MessageAttachments
                            attachments={regularAttachments}
                            previewUrls={previewUrls}
                            downloadingId={downloadingId}
                            onDownload={downloadAttachment}
                        />
                    </>
                )}
                <div className="mailbox-message-detail__footer">
                    <MessageReply
                        disabled={actionsBusy}
                        onReply={handleReply}
                    />
                </div>
            </CardBody>
            <MessageIntentAnalysisModal
                isOpen={preview.isOpen}
                session={preview.session}
                analysis={preview.analysis}
                isAnalyzing={preview.isAnalyzing}
                analyzingIntent={preview.analyzingIntent}
                analyzingProgress={preview.analyzingProgress}
                isApplying={preview.isApplying}
                onCancel={preview.handleCancel}
                onConfirm={preview.handleConfirm}
            />
        </Card>
    );
};

export default withNamespaces()(MessageDetail);
